/**
 * AWS AppSync Client Configuration
 * Replaces Apollo Client with AWS AppSync for GraphQL operations
 */
import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import { gql } from 'graphql-tag';
import { getAccessToken } from './token-storage.service.js';
import router from '../router/index.js';
import { clearTokens } from './token-storage.service.js';
/**
 * Get current Cognito token for AppSync authentication
 */
async function getCurrentToken() {
    const token = getAccessToken();
    return token || null;
}
/**
 * Create AppSync client instance (lazy initialization)
 */
let appsyncClientInstance = null;
function getAppSyncClient() {
    if (!appsyncClientInstance) {
        const url = import.meta.env.VITE_APP_AWS_APPSYNC_ENDPOINT;
        const region = import.meta.env.VITE_APP_AWS_REGION || 'us-east-2';
        if (!url) {
            throw new Error('AppSync endpoint not configured. Please set VITE_APP_AWS_APPSYNC_ENDPOINT environment variable.');
        }
        appsyncClientInstance = new AWSAppSyncClient({
            url,
            region,
            auth: {
                type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
                jwtToken: async () => {
                    const token = await getCurrentToken();
                    if (!token) {
                        throw new Error('No authentication token available');
                    }
                    return token;
                },
            },
            disableOffline: true,
        });
    }
    return appsyncClientInstance;
}
// Create a proxy that only initializes AppSync when actually used
export const appsyncClient = new Proxy({}, {
    get(_target, prop) {
        // Check if AppSync endpoint is configured before trying to initialize
        const url = import.meta.env.VITE_APP_AWS_APPSYNC_ENDPOINT;
        if (!url) {
            // Return a mock object that throws helpful errors
            if (prop === 'hydrated') {
                return () => Promise.resolve();
            }
            throw new Error('AppSync is not configured. Please set VITE_APP_AWS_APPSYNC_ENDPOINT environment variable.');
        }
        return getAppSyncClient()[prop];
    },
});
/**
 * Execute GraphQL query
 */
export async function executeQuery(query, variables) {
    const url = import.meta.env.VITE_APP_AWS_APPSYNC_ENDPOINT;
    if (!url) {
        throw new Error('AppSync is not configured. Please set VITE_APP_AWS_APPSYNC_ENDPOINT environment variable.');
    }
    try {
        const client = getAppSyncClient();
        const result = await client.query({
            query: gql(query),
            variables,
            fetchPolicy: 'network-only',
        });
        if (result.errors && result.errors.length > 0) {
            // Handle authentication errors
            const error = result.errors[0];
            if (error?.message?.includes('Unauthorized') || error?.message?.includes('UNAUTHENTICATED')) {
                clearTokens();
                router.push('/login');
            }
            throw new Error(error?.message || 'GraphQL error');
        }
        return result.data;
    }
    catch (error) {
        console.error('GraphQL query error:', error);
        // Handle network/auth errors
        if (error instanceof Error && error.message.includes('authentication')) {
            clearTokens();
            router.push('/login');
        }
        throw error;
    }
}
/**
 * Execute GraphQL mutation
 */
export async function executeMutation(mutation, variables) {
    const url = import.meta.env.VITE_APP_AWS_APPSYNC_ENDPOINT;
    if (!url) {
        throw new Error('AppSync is not configured. Please set VITE_APP_AWS_APPSYNC_ENDPOINT environment variable.');
    }
    try {
        const client = getAppSyncClient();
        const result = await client.mutate({
            mutation: gql(mutation),
            variables,
            fetchPolicy: 'no-cache',
        });
        if (result.errors && result.errors.length > 0) {
            // Handle authentication errors
            const error = result.errors[0];
            if (error?.message?.includes('Unauthorized') || error?.message?.includes('UNAUTHENTICATED')) {
                clearTokens();
                router.push('/login');
            }
            throw new Error(error?.message || 'GraphQL error');
        }
        return result.data;
    }
    catch (error) {
        console.error('GraphQL mutation error:', error);
        // Handle network/auth errors
        if (error instanceof Error && error.message.includes('authentication')) {
            clearTokens();
            router.push('/login');
        }
        throw error;
    }
}
//# sourceMappingURL=appsync-client.js.map